import { FC, FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import { FormLayout } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import FormField from "@/components/Forms/FormField";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import Button from "@/components/Button";
import { ImageToolStyleDescriptionResource } from "@/types/resources";

interface FormData {
  title: string;
  description: string;
}

interface Props {
  onClose: () => void;
  description?: ImageToolStyleDescriptionResource;
}

const ImageToolSettingsModal: FC<Props> = ({ onClose, description }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      title: description?.title ?? "",
      description: description?.description ?? "",
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        if (description) {
          await api.put(
            `/v1/image-generation-descriptions/${description.id}`,
            data
          );
        } else {
          await api.post(`/v1/image-generation-descriptions`, data);
        }

        await queryClient.invalidateQueries(["image_generation_descriptions"], {
          exact: true,
        });

        onClose();

        alerts.success(
          `Image style description was ${
            description ? "updates" : "created"
          } successfully`
        );
      } catch (err) {
        handleFormErrors<FormData>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${description ? "Edit" : "Add"} description`}
        onClose={onClose}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField label="Title" control={control} name="title" />
              <FormField
                label="Description"
                type="textarea"
                control={control}
                name="description"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {`${description ? "Update" : "Create"}`}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ImageToolSettingsModal;
